import { logoutAndRedirectToOpenWeb } from '@capture/client/API/login'
import { _ } from '@capture/client/assets/localization/util'
import { Checkbox } from '@capture/client/components/Common/Checkbox/Checkbox'
import { CheckboxList } from '@capture/client/components/Common/Checkbox/CheckboxList'
import { ExpandableSection } from '@capture/client/components/Common/ExpandableSection'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { PRIVACY_STATEMENT_LINK } from '@capture/client/config/constants'
import { getServiceProvider } from '@capture/client/API/HostProvider'
import type { AppService } from '@capture/client/API/services/AppService'
import BackArrowIcon from '@capture/capture-components/src/icons/arrow-back.svg?react'
import ChevronDownIcon from '@capture/capture-components/src/icons/chevron-down.svg?react'
import ChevronUpIcon from '@capture/capture-components/src/icons/chevron-up.svg?react'
import {
    colors,
    fontSize,
    mediaQueries,
} from '@capture/client/assets/styleConstants'
import {
    BorderedTextButton,
    FilledTextButton,
    IconTextButton,
} from '@capture/client/components/Common/IconTextButton'
import { LoadingPage } from '@capture/client/components/Common/LoadingPage'
import { PageWrapper } from '@capture/client/components/Common/PageWrapper'
import { RippleLoaderOverlay } from '@capture/client/components/Common/RippleLoaderOverlay'
import { ToggleSwitch } from '@capture/client/components/Common/toggleSwitch'
import { TwoAreasTopNavBar } from '@capture/client/components/Navigation/TwoAreasTopNavBar'
import { Settings, Takeout } from '@capture/client/routing/pages'
import { RecordUtil } from '@capture/client/utilities/dictionaryUtils'
import { useDispatch } from 'react-redux'
import ComposableDialogBox from './ComposableDialogBox'

const DialogBody = styled.div`
    font-size: ${fontSize.medium_16};
`

const ContentWrapper = styled.div`
    margin-top: 32px;
    width: 670px;
    background: white;
    border-radius: 8px;
    box-sizing: border-box;

    ${mediaQueries.mobile} {
        width: 100%;
    }
`

const ContentSection = styled.div`
    padding: 16px;
    & h3 {
        margin-bottom: 8px;
    }
    & p {
        margin-top: 8px;
        margin-bottom: 8px;
    }
`

const ConsentToggleWrapper = styled.div`
    position: relative;
    margin: -8px;
    padding: 8px;
    & > h3 {
        cursor: pointer;
    }
    & > h3 > div {
        margin-right: 8px;
    }
`

const CTAButtonWrapper = styled.div`
    padding-top: 16px;
`

const VerticalPadding = styled.div`
    padding-bottom: 32px;
`

const SectionHeader = styled.div`
    width: 100%;
    max-width: 640px;
    border-bottom: 1px solid ${colors.captureGrey300};
    padding: 16px 0;
    font-size: ${fontSize.medium_20};
    font-weight: 700;
    color: ${colors.captureGrey800};
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    cursor: pointer;
`

const SectionContent = styled.div`
    width: 100%;
    max-width: 640px;
    margin: 0 auto;
`
const PaddedMobileButton = styled(FilledTextButton)`
    min-width: fit-content;
    max-width: 40%;
    ${mediaQueries.mobile} {
        max-width: 100%;
    }
`
const Link = styled.a`
    color: ${colors.captureBlue};
`

const makeSettingsHeader: (
    title: string,
    cyKey?: string,
) => React.FunctionComponent<{ isExpanded: boolean }> = (title, cyKey) =>
    function SettingsHeader({ isExpanded }) {
        const Icon = isExpanded ? ChevronUpIcon : ChevronDownIcon
        return (
            <SectionHeader data-cy={cyKey}>
                {title}
                <Icon color={colors.captureGrey800} />
            </SectionHeader>
        )
    }

type SectionProps = {
    title: string
    expandedByDefault?: boolean
    cyKey?: string
    children?: React.ReactNode
}

const SettingsSection: React.FunctionComponent<SectionProps> = (props) => {
    return (
        <ExpandableSection
            header={makeSettingsHeader(props.title, props.cyKey)}
            expandedByDefault={props.expandedByDefault}>
            <SectionContent data-cy={props.cyKey}>
                {props.children}
            </SectionContent>
        </ExpandableSection>
    )
}

const withTSL = (s: string) =>
    s.replace('%company_name%', 'Telenor Software Lab AS')

const PrivacyStatementParagraph = () => {
    const [_s, beforeLink, linkText, afterLink] = withTSL(
        _('privacy_desc__processes_with_privacy_policy__format'),
    ).match('(.*)%link_start%(.*)%link_end%(.*)')!
    return (
        <p>
            {beforeLink}
            <Link href={PRIVACY_STATEMENT_LINK}>{linkText}</Link>
            {afterLink}
        </p>
    )
}

type TConsentVals = Record<DataProtectionConsentKey, boolean>
type ConsentValue = { val: boolean; isSaving: boolean; toggle: () => unknown }

const useConsentValues = (): Record<
    DataProtectionConsentKey,
    ConsentValue
> | null => {
    const [state, setState] = React.useState<TConsentVals | null>(null)
    const [serverState, setServerState] = React.useState<TConsentVals | null>(
        null,
    )

    React.useEffect(() => {
        getServiceProvider()
            .getAppServiceForLoggedInUserDefaults()
            .then((service) => service.getDataProtectionConsentValues())
            .then(({ data }) => {
                setState(data)
                setServerState(data)
            })
    }, [setState, setServerState])

    return React.useMemo(() => {
        if (state === null || serverState === null) {
            return null
        }

        const save = async ([key, value]: [
            DataProtectionConsentKey,
            boolean,
        ]) => {
            setState((prevState) => prevState && { ...prevState, [key]: value })
            try {
                const service =
                    await getServiceProvider().getAppServiceForLoggedInUserDefaults()
                await service.updateDataProtectionConsentValues({
                    [key]: value,
                })
                setServerState(
                    (prevState) => prevState && { ...prevState, [key]: value },
                )
            } catch (e) {
                console.error('when updateDataProtectionConsentValues:', e)
                setState(state)
            }
        }

        return RecordUtil.from(state)
            .map((val, key) => ({
                val,
                isSaving: val !== serverState[key],
                toggle: () => save([key, !val]),
            }))
            .getValue()
    }, [state, serverState, setState, setServerState])
}

const doNothing = () => {}
type ConsentToggleProps = {
    title: string
    description: string
    item: ConsentValue
}
const ConsentToggle = ({ item, title, description }: ConsentToggleProps) => (
    <ConsentToggleWrapper>
        <h3 onClick={item.toggle} onKeyUp={item.toggle} role="presentation">
            <ToggleSwitch isOn={item.val} onClick={doNothing} />
            {title}
        </h3>
        <p>{description}</p>
        {item.isSaving && <RippleLoaderOverlay />}
    </ConsentToggleWrapper>
)

enum Status {
    NOT_STARTED = 'NOT_STARTED',
    PENDING_SERVER = 'PENDING_SERVER',
    SUCCEEDED = 'SUCCEEDED',
    FAILED = 'FAILED',
}

const useAppServerCallback = (
    action: (service: AppService) => void,
): [() => void, Status] => {
    const [requestStatus, setRequested] = React.useState<Status>(
        Status.NOT_STARTED,
    )

    const doRequest = React.useCallback(async () => {
        setRequested(Status.PENDING_SERVER)
        try {
            const service =
                await getServiceProvider().getAppServiceForLoggedInUserDefaults()
            action(service)
            setRequested(Status.SUCCEEDED)
        } catch (e) {
            setRequested(Status.FAILED)
        }
    }, [action])
    return [doRequest, requestStatus]
}

interface OverlayProps {
    doClose: () => void
}

const AccessDataOverlay = (props: OverlayProps) => {
    const [requestData, requestStatus] = useAppServerCallback((s) =>
        s.downloadDataProtectionRequestedDataAccess(),
    )
    const [canFetch, setCanFetch] = React.useState<boolean | null>(null)
    React.useEffect(() => {
        getServiceProvider()
            .getAppServiceForLoggedInUserDefaults()
            .then((service) =>
                service.canDownloadDataProtectionRequestedDataAccess(),
            )
            .then(
                () => setCanFetch(true),
                () => setCanFetch(false),
            )
    }, [])

    const OverlayButtons = React.useMemo(
        () =>
            canFetch === false || requestStatus === Status.SUCCEEDED ? (
                <FilledTextButton text={_('close')} onClick={props.doClose} />
            ) : (
                <>
                    <>
                        <BorderedTextButton
                            text={_('cancel')}
                            color={colors.captureBlue}
                            onClick={props.doClose}
                            fontSize={fontSize.medium_16}
                        />
                        <FilledTextButton
                            text={_('privacy_request_a_copy_of_data')}
                            onClick={requestData}
                            data-cy="privacyOverlay_requestDataBtn"
                            isDisabled={!canFetch}
                            fontSize={fontSize.medium_16}
                        />
                    </>
                </>
            ),
        [canFetch, props.doClose, requestData, requestStatus],
    )

    const OverlayContent = React.useMemo(
        () =>
            canFetch === false ? (
                <p>
                    {_(
                        'metadata_recently_downloaded_please_wait__format',
                    ).replace('%d', '24')}
                </p>
            ) : (
                <>
                    <p>{_('per_gdpr_right_to_request')}</p>
                    <p>
                        {_('if_you_continue_metadata_download__format').replace(
                            '%d',
                            '24',
                        )}
                    </p>
                </>
            ),
        [canFetch],
    )

    const isLoading =
        canFetch === null || requestStatus === Status.PENDING_SERVER

    return (
        <ComposableDialogBox
            header={_('privacy_request_a_copy_of_data')}
            ButtonComponents={OverlayButtons}
            loading={isLoading}
            cyKey="privacyOverlay_requestData">
            <DialogBody>{OverlayContent}</DialogBody>
        </ComposableDialogBox>
    )
}

const DeleteAccountOverlay = (props: OverlayProps) => {
    const [requestDeletion, requestStatus] = useAppServerCallback((s) =>
        s.sendDataProtectionRequestForAccountDeletion(),
    )
    const dispatch = useDispatch()
    const logoutToOpenWeb = React.useCallback(
        () => logoutAndRedirectToOpenWeb(dispatch),
        [dispatch],
    )
    const OverlayButtons = React.useMemo(() => {
        switch (requestStatus) {
            case Status.NOT_STARTED:
            case Status.PENDING_SERVER:
                return (
                    <>
                        <BorderedTextButton
                            text={_('cancel')}
                            color={colors.captureBlue}
                            onClick={props.doClose}
                            data-cy="privacyOverlay_cancelDeletionBtn"
                        />
                        <FilledTextButton
                            text={_('delete_account')}
                            fillColor={colors.warningRed}
                            onClick={requestDeletion}
                            data-cy="privacyOverlay_confirmDeletionBtn"
                        />
                    </>
                )
            case Status.SUCCEEDED:
                return (
                    <FilledTextButton
                        text={_('close')}
                        onClick={logoutToOpenWeb}
                        data-cy="privacyOverlay_deletionSuccessfulCloseBtn"
                    />
                )
            case Status.FAILED:
                return (
                    <>
                        <BorderedTextButton
                            text={_('close')}
                            color={colors.captureBlue}
                            onClick={props.doClose}
                        />
                        <FilledTextButton
                            text={_('retry')}
                            onClick={requestDeletion}
                        />
                    </>
                )
        }
    }, [requestStatus, props.doClose, requestDeletion, logoutToOpenWeb])

    const OverlayContent = React.useMemo(() => {
        switch (requestStatus) {
            case Status.NOT_STARTED:
            case Status.PENDING_SERVER:
                return (
                    <>
                        <p>{_('delete_your_account__info4')}</p>
                        <p>
                            <b>{_('delete_your_account__info5')}</b>
                        </p>
                        <p>{_('delete_your_account__info6')}</p>
                    </>
                )
            case Status.SUCCEEDED:
                return (
                    <>
                        <p>
                            <b>{_('delete_your_account__success1')}</b>
                        </p>
                        <p>
                            <b>
                                {_(
                                    'delete_your_account__success2__format',
                                ).replace('%d', '30')}
                            </b>
                        </p>
                    </>
                )
            case Status.FAILED:
                return (
                    <>
                        <p>{_('delete_your_account__failed1')}</p>
                        <p>{_('generic_explanation_for_why_it_may_failed')}</p>
                    </>
                )
        }
    }, [requestStatus])

    return (
        <ComposableDialogBox
            header={_('delete_your_account')}
            ButtonComponents={OverlayButtons}
            loading={requestStatus === Status.PENDING_SERVER}
            cyKey="privacyOverlay_confirmDeletion">
            {OverlayContent}
        </ComposableDialogBox>
    )
}

type PrivacyPageProps = {
    insideMobileApp: boolean
}

export const PrivacyPage = ({ insideMobileApp }: PrivacyPageProps) => {
    const consentValues = useConsentValues()
    const [currentActionOverlay, _setActionOverlay] = React.useState<
        'export' | 'delete' | null
    >(null)
    const navigate = useNavigate()
    const ActionOverlayTriggers = {
        export: () => _setActionOverlay('export'),
        delete: () => _setActionOverlay('delete'),
        none: () => _setActionOverlay(null),
    }
    const navigateToSettings = () => navigate(Settings.url)
    const navigateToTakeout = () => navigate(Takeout.url)

    if (consentValues === null) {
        return <LoadingPage />
    }
    const isUserConsentMigrated = consentValues?.face_grouping

    const backButton =
        insideMobileApp === true ? null : (
            <IconTextButton
                onClick={navigateToSettings}
                text={_('settings')}
                icon={BackArrowIcon}
                fontSize={fontSize.small_14}
            />
        )

    const navBar = <TwoAreasTopNavBar left={backButton} />

    return (
        <PageWrapper navBar={navBar} hideFooter={insideMobileApp === true}>
            <ContentWrapper>
                <ContentSection>
                    <SettingsSection title={_('stay_up_to_date')}>
                        <p>{_('privacy_desc__let_you_know_about_updates')}.</p>
                        <p>{withTSL(_('can_contact_me_via__format'))}</p>
                        <CheckboxList>
                            <Checkbox
                                checked={
                                    consentValues.stay_up_to_date__email.val
                                }
                                onClick={
                                    consentValues.stay_up_to_date__email.toggle
                                }
                                disabled={
                                    consentValues.stay_up_to_date__email
                                        .isSaving
                                }>
                                {_('email')}
                            </Checkbox>
                            <Checkbox
                                checked={
                                    consentValues
                                        .stay_up_to_date__push_notification.val
                                }
                                onClick={
                                    consentValues
                                        .stay_up_to_date__push_notification
                                        .toggle
                                }
                                disabled={
                                    consentValues
                                        .stay_up_to_date__push_notification
                                        .isSaving
                                }>
                                {_('push_notification')}
                            </Checkbox>
                            <Checkbox
                                checked={consentValues.stay_up_to_date__sms.val}
                                onClick={
                                    consentValues.stay_up_to_date__sms.toggle
                                }
                                disabled={
                                    consentValues.stay_up_to_date__sms.isSaving
                                }>
                                {_('SMS')}
                            </Checkbox>
                        </CheckboxList>
                    </SettingsSection>
                </ContentSection>

                <ContentSection>
                    <SettingsSection title={_('consents')}>
                        {isUserConsentMigrated ? (
                            <>
                                <ConsentToggle
                                    title={_('face_grouping')}
                                    description={withTSL(
                                        _(
                                            'privacy_desc__can_process_face_details__format',
                                        ),
                                    )}
                                    item={consentValues.face_grouping}
                                />
                                <ConsentToggle
                                    title={_(
                                        'advanced_filtering_and_categorization',
                                    )}
                                    description={withTSL(
                                        _(
                                            'privacy_desc__can_process_images_advanced_filtering__format',
                                        ),
                                    )}
                                    item={
                                        consentValues.advanced_filtering_and_categorization
                                    }
                                />
                            </>
                        ) : (
                            <ConsentToggle
                                title={_('facial_recognition')}
                                description={withTSL(
                                    _(
                                        'privacy_desc__can_process_images_categorisation__format',
                                    ),
                                )}
                                item={consentValues.facial_recognition}
                            />
                        )}

                        <ConsentToggle
                            title={_('help_us_improve')}
                            description={withTSL(
                                _(
                                    'privacy_desc__can_process_data_for_improvement__format',
                                ),
                            )}
                            item={consentValues.help_us_improve}
                        />
                    </SettingsSection>
                </ContentSection>

                <ContentSection>
                    <SettingsSection title={_('access_your_data')}>
                        <PrivacyStatementParagraph />
                        <p>
                            {withTSL(
                                _(
                                    'privacy_desc__download_personal_information__format',
                                ),
                            )}{' '}
                        </p>
                        <CTAButtonWrapper>
                            <PaddedMobileButton
                                text={_('privacy_request_a_copy_of_data')}
                                onClick={ActionOverlayTriggers.export}
                            />
                        </CTAButtonWrapper>
                        <p style={{ paddingTop: '30px' }}>
                            {_('privacy_download_all')}
                        </p>
                        <CTAButtonWrapper>
                            <PaddedMobileButton
                                text={_('privacy_download_all_button')}
                                onClick={navigateToTakeout}
                            />
                        </CTAButtonWrapper>
                    </SettingsSection>
                </ContentSection>

                <ContentSection>
                    <SettingsSection
                        title={_('delete_your_account')}
                        cyKey="deleteCaptureAccountExpandable">
                        <p>
                            {withTSL(
                                _('you_can_request_delete_account__format'),
                            )}
                        </p>
                        <p>
                            <b>
                                {_(
                                    'delete_account_includes_irreversible_deletion',
                                )}
                            </b>
                        </p>
                        <p>
                            {_('it_may_take_up_to_days__format').replace(
                                '%d',
                                '30',
                            )}
                        </p>
                        <CTAButtonWrapper>
                            <PaddedMobileButton
                                text={_('request_deletion')}
                                fillColor={colors.warningRed}
                                onClick={ActionOverlayTriggers.delete}
                                data-cy="requestAccountDeletionBtn"
                            />
                        </CTAButtonWrapper>
                    </SettingsSection>
                </ContentSection>

                {insideMobileApp === true && <VerticalPadding />}
            </ContentWrapper>
            {currentActionOverlay === 'export' && (
                <AccessDataOverlay doClose={ActionOverlayTriggers.none} />
            )}
            {currentActionOverlay === 'delete' && (
                <DeleteAccountOverlay doClose={ActionOverlayTriggers.none} />
            )}
        </PageWrapper>
    )
}

export const PrivacyPageForMobileApplication = () => {
    return <PrivacyPage insideMobileApp />
}
